<template>
  <v-row justify="center">
    <!-- add empty row to fit to page on mobile -->
    <v-row class="ma-0 pa-0"></v-row>

    <!-- Change Password -->
    <v-dialog v-model="passwordDialog" persistent max-width="500px">
      <v-card>
        <v-card-title class="primary white--text">
          {{ $t("userEditDialog.changePasswordTitle") }}
          <v-spacer></v-spacer>
        </v-card-title>

        <v-card-text class="pt-5">
          <v-form ref="passwordForm">
            <v-row>
              <v-col cols="12">
                <div class="password-container">
                  <v-text-field
                    v-model="editedItem.password"
                    :counter="40"
                    :rules="[
                      rules.required,
                      rules.maxLength(40),
                      checkPasswordStrength,
                    ]"
                    v-bind:label="$t('userEditDialog.newPasswordLabel')"
                    :type="showpassword ? 'text' : 'password'"
                    outlined
                    :append-icon="
                      showpassword ? 'visibility' : 'visibility_off'
                    "
                    @click:append="showpassword = !showpassword"
                  ></v-text-field>

                  <!-- Password Strength Feedback -->
                  <v-progress-linear
                    v-if="editedItem.password"
                    :value="passwordStrengthScore * 25"
                    height="5"
                    :color="passwordStrengthColor"
                    class="mb-4 password-progress"
                  ></v-progress-linear>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pb-5 pr-5">
          <v-spacer></v-spacer>
          <v-btn
            :disabled="isSavingPassword"
            text
            rounded
            @click="closePasswordDialog"
            >{{ $t("button.cancelBtn") }}</v-btn
          >
          <v-btn
            outlined
            rounded
            :loading="isSavingPassword"
            color="primary"
            @click="changePasswordConfirmed"
            @keydown.enter="changePasswordConfirmed"
            >{{ $t("button.saveBtn") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit profile -->
    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card>
        <v-alert v-if="error" tile type="error">{{ error }}</v-alert>

        <v-card-title class="primary white--text">
          {{ formTitle }}
          <v-spacer></v-spacer>
          <v-btn
            v-if="hasPermission('change_customuser') && object"
            outlined
            rounded
            small
            class="ml-2"
            dark
            @click="onChangePassword"
            >{{ $t("userEditDialog.changePasswordBtn") }}</v-btn
          >
        </v-card-title>

        <v-card-text class="mt-5 pt-5">
          <v-form ref="form">
            <v-row>
              <v-col
                cols="12"
                md="8"
                v-if="me.isSuperuser || me.isPartnerUser"
                class="my-0 py-0"
              >
                <label
                  ><h4>
                    {{ $tc("label.type", 1) }}
                  </h4></label
                >
                <v-radio-group
                  :rules="[rules.required]"
                  class="mt-1"
                  row
                  v-model="editedItem.userType"
                >
                  <v-radio
                    :label="`${$tc('label.tenant', 1)} ${$tc('label.user', 1)}`"
                    value="tenantUser"
                  ></v-radio>
                  <v-radio
                    :label="`${$tc('label.partner', 1)} ${$tc(
                      'label.user',
                      1
                    )}`"
                    value="partnerUser"
                  ></v-radio>
                </v-radio-group>
              </v-col>
              <v-col
                cols="12"
                md="4"
                v-if="me.isSuperuser && editedItem.userType === 'tenantUser'"
              >
                <v-switch
                  :label="'Superuser'"
                  class="mt-3"
                  hide-details
                  v-model="editedItem.isSuperuser"
                ></v-switch>
              </v-col>
              <!-- Tenant -->
              <v-col
                cols="12"
                v-if="
                  (me.isSuperuser || me.isPartnerUser) &&
                  editedItem.userType === 'tenantUser'
                "
                class="my-0 py-0"
              >
                <v-autocomplete
                  v-model="editedItem.tenantNodeId"
                  :items="tenants.edges"
                  item-text="node.tenantName"
                  item-value="node.id"
                  :loading="$apollo.queries.tenants.loading"
                  :rules="[rules.required]"
                  v-bind:label="$t('userEditDialog.tenantLabel')"
                  outlined
                  clearable
                >
                </v-autocomplete>
              </v-col>

              <v-col
                cols="12"
                v-if="me.isSuperuser && editedItem.userType === 'partnerUser'"
                class="my-0 py-0"
              >
                <v-autocomplete
                  v-model="editedItem.partnerNodeId"
                  :items="allPartners.edges"
                  item-text="node.name"
                  item-value="node.id"
                  :loading="$apollo.queries.allPartners.loading"
                  :rules="[rules.required]"
                  :label="$tc('label.partner', 1)"
                  outlined
                  clearable
                >
                </v-autocomplete>
              </v-col>

              <v-col cols="6" class="my-0 py-0">
                <v-text-field
                  v-model="editedItem.firstName"
                  :counter="100"
                  :rules="[rules.required, rules.maxLength(100)]"
                  v-bind:label="$t('userEditDialog.firstNameLabel')"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="6" class="my-0 py-0">
                <v-text-field
                  v-model="editedItem.lastName"
                  :counter="100"
                  :rules="[rules.required, rules.maxLength(100)]"
                  v-bind:label="$t('userEditDialog.lastNameLabel')"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="my-0 py-0">
                <v-text-field
                  v-model="editedItem.email"
                  :counter="100"
                  :rules="[rules.required, rules.maxLength(100)]"
                  v-bind:label="$t('userEditDialog.emailLabel')"
                  type="email"
                  outlined
                ></v-text-field>
              </v-col>

              <!-- Identity provider -->
              <v-col cols="12" class="my-0 py-0">
                <v-autocomplete
                  v-model="editedItem.identityProvider"
                  :items="identityProviders"
                  item-text="name"
                  item-value="id"
                  :rules="[rules.required]"
                  v-bind:label="$t('userEditDialog.identityProviderLabel')"
                  outlined
                  clearable
                >
                </v-autocomplete>
              </v-col>

              <!-- groups -->
              <v-col v-if="!editedItem.isSuperuser" cols="12" class="my-0 py-0">
                <v-autocomplete
                  v-model="editedItem.selectedGroups"
                  :items="groups.edges"
                  item-text="node.name"
                  item-value="node.id"
                  :loading="$apollo.queries.groups.loading"
                  :rules="[rules.required]"
                  v-bind:label="$t('userEditDialog.groupsLabel')"
                  outlined
                  clearable
                  multiple
                >
                </v-autocomplete>
              </v-col>
              <v-col cols="12" v-if="!object" class="my-0 py-0">
                <div class="password-container">
                  <v-text-field
                    v-model="editedItem.password"
                    :counter="40"
                    :rules="[
                      rules.required,
                      rules.maxLength(40),
                      checkPasswordStrength,
                    ]"
                    v-bind:label="$t('userEditDialog.passwordLabel')"
                    :type="showpassword ? 'text' : 'password'"
                    outlined
                    :append-icon="
                      showpassword ? 'visibility' : 'visibility_off'
                    "
                    @click:append="showpassword = !showpassword"
                  ></v-text-field>

                  <!-- Password Strength Feedback -->
                  <v-progress-linear
                    v-if="editedItem.password"
                    :value="passwordStrengthScore * 25"
                    height="5"
                    :color="passwordStrengthColor"
                    class="mb-4 password-progress-edit"
                  ></v-progress-linear>
                </div>
              </v-col>

              <v-col
                cols="6"
                v-if="editedItem.identityProvider == 'INSURANCE_DATA'"
              >
                <v-switch
                  v-model="editedItem.require2fa"
                  v-bind:label="$t('userEditDialog.require2faLabel')"
                  class="my-0 py-0"
                  hide-details
                ></v-switch>
              </v-col>
              <v-col
                v-if="
                  !isNewUser && editedItem.identityProvider == 'INSURANCE_DATA'
                "
                cols="12"
              >
                <v-row class="align-center">
                  <v-col
                    v-if="editedItem.enabled2fa && !editedItem.clear2fa"
                    cols="6"
                    class="mb-0 pb-0"
                  >
                    <v-icon color="success">verified_user</v-icon>
                    {{ $t("userEditDialog.2faEnabledText") }}
                  </v-col>
                  <v-col
                    v-else-if="editedItem.enabled2fa && editedItem.clear2fa"
                    cols="6"
                    class="mb-0 pb-0"
                  >
                    <v-icon color="error">highlight_off</v-icon>
                    {{ $t("userEditDialog.2faResetText") }}
                  </v-col>
                  <v-col v-else cols="6" class="mb-0 pb-0">
                    <v-icon color="warning">highlight_off</v-icon>
                    {{ $t("userEditDialog.2faNotSetText") }}
                  </v-col>
                  <v-col
                    v-if="editedItem.enabled2fa"
                    cols="6"
                    class="mb-0 pb-0"
                  >
                    <v-checkbox
                      v-model="editedItem.clear2fa"
                      color="error"
                      v-bind:label="$t('userEditDialog.2faResetLabel')"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pb-5 pr-5">
          <v-spacer></v-spacer>
          <v-btn :disabled="isSaving" text rounded @click="closeUserDialog">{{
            $t("button.cancelBtn")
          }}</v-btn>
          <v-btn
            outlined
            rounded
            :loading="isSaving"
            color="primary"
            @click="saveUser"
            >{{ $t("button.saveBtn") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import zxcvbn from "zxcvbn";

export default {
  name: "user-edit-dialog",
  props: ["dialog", "object"],
  components: {},

  apollo: {
    allPartners: {
      query: gql`
        query allPartners {
          allPartners(orderBy: ["name"]) {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      `,
      variables() {},
      fetchPolicy: "cache-and-network",
      update: (data) => data.allPartners,
      skip: true,
      pollInterval: 60000,
    },

    tenants: {
      query: gql`
        query tenants {
          tenants {
            edges {
              node {
                id
                tenantName
              }
            }
          }
        }
      `,
      variables() {},
      // Additional options here
      fetchPolicy: "cache-and-network",
      update: (data) => data.tenants,
      skip: true,
      pollInterval: 60000, // ms
    },

    groups: {
      query: gql`
        query groups {
          groups {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      `,
      variables() {},
      // Additional options here
      fetchPolicy: "cache-and-network",
      update: (data) => data.groups,
      skip: true,
      pollInterval: 60000, // ms
    },
  },

  data() {
    return {
      allPartners: {},
      error: null,
      isSaving: false,
      isSavingPassword: false,
      passwordDialog: false,
      tenantDialog: false,
      showpassword: false,
      passwordStrengthFeedback: "",
      passwordStrengthScore: 0,
      tenants: {},
      groups: {},
      defaultItem: {
        id: null,
        email: null,
        password: Math.random().toString(36).slice(-10),
        firstName: null,
        lastName: null,
        isSuperuser: false,
        require2fa: true,
        enabled2fa: false,
        clear2fa: false,
        tenantNodeId: "",
        selectedGroups: [],
        identityProvider: null,
        userType: "tenantUser",
      },
      editedItem: {
        id: null,
        email: null,
        password: null,
        firstName: null,
        lastName: null,
        isSuperuser: false,
        require2fa: false,
        enabled2fa: false,
        clear2fa: false,
        tenantNodeId: "",
        selectedGroups: [],
        identityProvider: null,
        userType: null,
      },

      identityProviders: [
        {
          id: "INSURANCE_DATA",
          name: "Insurance Data",
        },
        {
          id: "MICROSOFT_AZURE_AD",
          name: "Microsoft Azure AD",
        },
      ],

      rules: {
        required: (v) =>
          !helper.isEmpty(v) || this.$t("passwordReset.isRequired"),
        listRequired: (v) =>
          (v || "").length >= 1 || this.$t("passwordReset.isRequired"),
        maxLength: (len) => (v) =>
          (v || "").length <= len || this.$t("passwordReset.maxLength"),
      },
    };
  },
  computed: {
    isNewUser() {
      return this.object ? false : true;
    },
    formTitle() {
      return this.object
        ? this.$t("userEditDialog.editTitle")
        : this.$t("userEditDialog.newTitle");
    },
    me() {
      return this.$store.state.user.me;
    },
    passwordStrengthColor() {
      switch (this.passwordStrengthScore) {
        case 0:
          return "red";
        case 1:
          return "deep-orange";
        case 2:
          return "yellow";
        case 3:
          return "light-green";
        case 4:
          return "green";
        default:
          return "grey";
      }
    },
  },
  watch: {
    // reset form when dialog open or close
    dialog(val) {
      // if open dialog, initialize selections
      if (val) {
        this.$apollo.queries.allPartners.skip = false;
        this.$apollo.queries.tenants.skip = false;
        this.$apollo.queries.groups.skip = false;
      }

      this.resetForm();
    },
  },
  created() {
    this.hasPermission = helper.hasPermission;

    this.fetchMe();
  },
  methods: {
    fetchMe() {
      this.$store
        .dispatch("user/fetchMe")
        .then(() => {})
        .catch((error) => {
          console.log(error);
        });
    },

    resetForm() {
      // reset form state
      this.error = null;
      this.isSaving = false;
      this.panel = 0;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      // when editing, set form with object
      if (this.dialog && this.object) {
        this.editedItem.id = this.object.id;
        this.editedItem.email = this.object.email;
        this.editedItem.firstName = this.object.firstName;
        this.editedItem.lastName = this.object.lastName;
        this.editedItem.isSuperuser = this.object.isSuperuser;
        this.editedItem.require2fa = this.object.require2fa;
        this.editedItem.enabled2fa = this.object.enabled2fa;
        this.editedItem.clear2fa = false;
        this.editedItem.tenantNodeId = this.object.tenant?.id;
        this.editedItem.selectedGroups = this.object.groups
          ? this.object.groups.edges.map((item) => {
              return item.node.id;
            })
          : [];
        this.editedItem.identityProvider = this.object.identityProvider;
        this.editedItem.partnerNodeId = this.object.partner?.id;
        this.editedItem.userType = this.object.isPartnerUser
          ? "partnerUser"
          : "tenantUser";
      }
      // when new or closing, set form with default
      else {
        this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
      }
    },

    onChangePassword() {
      this.passwordDialog = true;
    },

    changePasswordConfirmed() {
      if (!this.$refs.passwordForm.validate()) {
        return;
      }

      // set form state
      this.error = null;
      this.isSavingPassword = true;

      // prepare api call payload
      var payload = {
        id: this.editedItem.id,
        newPassword: this.editedItem.password,
      };

      this.$store
        .dispatch("user/changePassword", payload)
        .then(() => {
          this.closePasswordDialog();

          // show snackbar
          const payload = {
            color: "success",
            message: `Password successfully changed`,
          };
          this.$store.dispatch("snackbar/showMessage", payload);
        })
        .catch((error) => {
          console.log(error);

          this.error = error.graphQLErrors
            .map((error) => error.message)
            .join(", ");
        })
        .finally(() => {
          this.isSavingPassword = false;
        });
    },

    closePasswordDialog() {
      this.passwordDialog = false;
      this.editedItem.password = null;
      this.$refs.passwordForm.resetValidation();
    },

    closeUserDialog() {
      this.$emit("update:dialog", false);
      this.resetForm();
    },

    saveUser() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // set form state
      this.error = null;
      this.isSaving = true;

      // prepare api call payload
      var payload = {
        email: this.editedItem.email,
        firstName: this.editedItem.firstName,
        lastName: this.editedItem.lastName,
        isSuperuser: this.editedItem.isSuperuser,
        require2fa: this.editedItem.require2fa,
        tenantNodeId: this.editedItem.tenantNodeId,
        groups: this.editedItem.selectedGroups,
        identityProvider: this.editedItem.identityProvider,
        isPartnerUser: this.editedItem.userType == "partnerUser" ? true : false,
        partnerNodeId: this.editedItem.partnerNodeId,
      };

      // if editing
      if (this.object) {
        // prepare graphql mutation payload
        payload.id = this.editedItem.id;

        payload = {
          ...payload,
          clear2fa: this.editedItem.clear2fa,
        };

        // update
        this.$apollo
          .mutate({
            // Query
            mutation: gql`
              mutation updateUser($input: UpdateUserInput!) {
                updateUser(input: $input) {
                  user {
                    id
                    email
                    firstName
                    lastName
                    isSuperuser
                    require2fa
                    enabled2fa
                    tenant {
                      id
                      tenantName
                    }
                    identityProvider
                  }
                }
              }
            `,
            // Parameters
            variables: {
              input: payload,
            },
          })
          .then((response) => {
            // update parent object
            this.$emit("changed", response);
            this.closeUserDialog();

            // show snackbar
            const payload = {
              color: "success",
              message: `User successfully edited`,
            };
            this.$store.dispatch("snackbar/showMessage", payload);
          })
          .catch((error) => {
            this.error = error.graphQLErrors[0].message;
            this.isSaving = false;
          })
          .finally(() => {});
      }
      // new
      else {
        payload.password = this.editedItem.password;

        this.$apollo
          .mutate({
            // Query
            mutation: gql`
              mutation createUser($input: CreateUserInput!) {
                createUser(input: $input) {
                  user {
                    id
                  }
                }
              }
            `,
            // Parameters
            variables: {
              input: payload,
            },
          })
          .then((response) => {
            this.$emit("changed", response);
            this.closeUserDialog();

            // show snackbar
            const payload = {
              color: "success",
              message: `User successfully added`,
            };
            this.$store.dispatch("snackbar/showMessage", payload);
          })
          .catch((error) => {
            console.log(error);

            this.error = error.graphQLErrors
              .map((error) => error.message)
              .join(", ");
            this.isSaving = false;
          })
          .finally(() => {});
      }
    },

    addTenant() {
      this.tenantDialog = true;
    },
    tenantAdded(item) {
      this.editedItem.tenantNodeId = item.id;
    },
    // Custom password strength check
    checkPasswordStrength(value) {
      if (!value) {
        this.passwordStrengthFeedback = "";
        this.passwordStrengthScore = 0;
        return true; // No password to check yet
      }

      const result = zxcvbn(value);
      this.passwordStrengthScore = result.score;

      if (this.passwordStrengthScore < 3) {
        this.passwordStrengthFeedback = this.$t(
          "passwordReset.passwordTooLong"
        );
        return this.passwordStrengthFeedback; // Return feedback as the validation message
      }

      this.passwordStrengthFeedback = ""; // Clear feedback if the password is strong enough
      return true; // Validation passed
    },
  },
};
</script>
